'use client';

import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import {cn} from '@purpose/ui/lib/utils';

interface DualRangeSliderProps extends React.ComponentProps<
  typeof SliderPrimitive.Root
> {
  labelPosition?: 'top' | 'bottom';
  label?: (value: number | undefined) => React.ReactNode;
  rangeClassName?: string;
  trackClassName?: string;
}

const RangeSlider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  DualRangeSliderProps
>(({className, label, labelPosition = 'top', ...props}, ref) => {
  const initialValue = Array.isArray(props.value)
    ? props.value
    : [props.min, props.max];

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex w-full touch-none items-center select-none',
        className
      )}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          'bg-primary-60 relative h-2 w-full grow overflow-hidden rounded-full',
          props.trackClassName
        )}
      >
        <SliderPrimitive.Range
          className={cn(
            'bg-primary-base absolute h-full',
            props.rangeClassName
          )}
        />
      </SliderPrimitive.Track>
      {initialValue.map((value, index) => (
        <React.Fragment key={index}>
          <SliderPrimitive.Thumb className="border-primary-80 bg-primary-80 ring-offset-background focus-visible:ring-ring relative block h-5 w-5 rounded-full border-2 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50">
            {label && (
              <span
                className={cn(
                  'absolute flex w-full justify-center',
                  labelPosition === 'top' && '-top-7',
                  labelPosition === 'bottom' && 'top-4'
                )}
              >
                {label(value)}
              </span>
            )}
          </SliderPrimitive.Thumb>
        </React.Fragment>
      ))}
    </SliderPrimitive.Root>
  );
});
RangeSlider.displayName = 'RangeSlider';

export {RangeSlider};
